import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canAssignRole, hasCapability, type Actor, type Grant } from '@prooffleet/contracts';
import { IconUserPlus } from '@tabler/icons-react';
import { ApiError, api, useSession } from '@prooffleet/client';
import { EmptyState, PageHeader, TableSkeleton } from '../components/Page.js';

interface Member {
  userId: string;
  displayName: string;
  email: string | null;
  phone: string | null;
  status: string;
  roleId: string;
  roleKey: string;
  roleName: string;
  roleLevel: number;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
}

interface RoleRow {
  _id: string;
  name: string;
  level: number;
  grants: Grant[];
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  invited: 'Invited',
  suspended: 'Suspended',
};

export function Users(): React.JSX.Element {
  const queryClient = useQueryClient();
  const actor = useSession((s) => s.actor);
  const canGrant = actor !== null && hasCapability(actor, 'user:role:grant');

  const [target, setTarget] = useState<Member | null>(null);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState<string | null>(null);
  /**
   * Ссылка показывается один раз и не хранится.
   *
   * Рассылка приглашений почтой не сделана, поэтому ссылку передаёт человек.
   * Токен одноразовый и с коротким сроком: показать его повторно нельзя,
   * можно только выпустить новое приглашение.
   */
  const [inviteLink, setInviteLink] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['members'],
    queryFn: () => api<{ members: Member[] }>('/users'),
  });
  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api<{ roles: RoleRow[] }>('/roles'),
  });

  const invite = useMutation({
    mutationFn: () =>
      api<{ token: string; expiresAt: string }>('/auth/invite', {
        method: 'POST',
        body: {
          email: inviteEmail,
          displayName: inviteName,
          roleId: inviteRoleId,
          siteIds: null,
        },
      }),
    onSuccess: (result) => {
      setInviteLink(`${window.location.origin}/accept-invite?token=${result.token}`);
      setError(null);
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Could not send the invitation');
    },
  });

  const assign = useMutation({
    mutationFn: async () => {
      setError(null);
      return api('/roles/assign', {
        method: 'POST',
        body: { userId: target?.userId, roleId, siteIds: null },
      });
    },
    onSuccess: async () => {
      setTarget(null);
      setRoleId(null);
      await queryClient.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Could not assign the role');
    },
  });

  const members = data?.members ?? [];
  // Сводка сверху: дыру во втором факторе менеджер должен увидеть до того,
  // как начнёт читать таблицу построчно.
  const gaps = members.filter((m) => m.roleLevel >= 30 && !m.mfaEnabled).length;

  const canInvite = actor !== null && hasCapability(actor, 'user:invite');
  const canImpersonate = actor !== null && hasCapability(actor, 'support:impersonate');

  const [impTarget, setImpTarget] = useState<Member | null>(null);
  const [impReason, setImpReason] = useState('');

  /**
   * Вход от имени пользователя.
   *
   * После смены сессии страница перезагружается целиком: половина приложения
   * держит закэшированные данные прежней личности, и мягкий переход показал бы
   * менеджеру ленту водителя вперемешку со своей.
   */
  const impersonate = useMutation({
    mutationFn: () =>
      api<{ accessToken: string; actor: Actor }>('/auth/impersonate', {
        method: 'POST',
        body: { targetUserId: impTarget?.userId, reason: impReason },
      }),
    onSuccess: (result) => {
      useSession.getState().setSession(result.accessToken, result.actor);
      window.location.assign('/');
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Could not start impersonation');
    },
  });

  /** Роли, которые редактор вправе выдать: не выше своей и не шире своих прав. */
  const assignable = (roles?.roles ?? []).filter(
    (role) => actor !== null && canAssignRole(actor, { level: role.level, grants: role.grants }),
  );

  return (
    <Stack gap={0}>
      <PageHeader
        title="People"
        count={members.length}
        hint="Roles with portal access must have two-factor enabled. Anyone missing it is flagged here."
        action={
          canInvite ? (
            <Button
              leftSection={<IconUserPlus size={15} stroke={1.8} />}
              onClick={() => {
                setInviteEmail('');
                setInviteName('');
                setInviteRoleId(null);
                setInviteLink(null);
                setError(null);
                setInviteOpen(true);
              }}
            >
              Invite
            </Button>
          ) : undefined
        }
      />

      {gaps > 0 && (
        <Alert color="red" mb="md">
          {gaps === 1
            ? '1 person with portal access has no second factor.'
            : `${String(gaps)} people with portal access have no second factor.`}
        </Alert>
      )}

      {isLoading && <TableSkeleton rows={4} cols={5} />}

      {!isLoading && members.length === 0 && (
        <EmptyState title="No people yet" hint="Invite teammates to give them access to this fleet." />
      )}

      {!isLoading && members.length > 0 && (
        <Table.ScrollContainer minWidth={720}>
          <Table className="pf-table" layout="fixed">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w="26%">Name</Table.Th>
                <Table.Th w="24%">Contact</Table.Th>
                <Table.Th w="20%">Role</Table.Th>
                <Table.Th w="15%">Two-factor</Table.Th>
                <Table.Th w="15%" ta="right" />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {members.map((member) => {
                const missingMfa = member.roleLevel >= 30 && !member.mfaEnabled;
                return (
                  <Table.Tr key={member.userId}>
                    <Table.Td>
                      <Text fw={580} fz="sm" lh={1.35}>
                        {member.displayName}
                      </Text>
                      {member.status !== 'active' && (
                        <Text className="pf-eyebrow" mt={2}>
                          {STATUS_LABEL[member.status] ?? member.status}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text className="pf-mono" truncate>
                        {member.email ?? member.phone ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {/* Роль — категория, а не тяжесть: цвет здесь означал бы
                          состояние и конкурировал бы с критическим дефектом. */}
                      <Text fz="sm" c="gray.7">
                        {member.roleName}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {missingMfa ? (
                        // Дыра обязана быть заметна до происшествия, а не после.
                        <Badge color="red.8" variant="filled" size="sm">
                          missing
                        </Badge>
                      ) : (
                        <Text fz="sm" c="dimmed">
                          {member.mfaEnabled ? 'enabled' : 'not required'}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      <Group gap={4} justify="flex-end" wrap="nowrap" className="pf-row-action">
                      {canImpersonate && member.roleLevel < (actor?.level ?? 0) && (
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          color="gray"
                          onClick={() => {
                            setImpTarget(member);
                            setImpReason('');
                            setError(null);
                          }}
                        >
                          View as
                        </Button>
                      )}
                      <Button
                        size="compact-xs"
                        variant="default"
                        disabled={!canGrant}
                        onClick={() => {
                          setTarget(member);
                          setRoleId(member.roleId);
                        }}
                      >
                        Change role
                      </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <Modal
        opened={target !== null}
        onClose={() => setTarget(null)}
        title={`Role for ${target?.displayName ?? ''}`}
      >
        <Stack gap="md">
          <Alert color="blue" variant="light" fz="sm">
            You can only grant a role no higher than your own and no broader than your own
            permissions. Changing a role revokes the user's active sessions immediately.
          </Alert>

          <Select
            label="Role"
            data={assignable.map((r) => ({ value: r._id, label: r.name }))}
            value={roleId}
            onChange={setRoleId}
          />

          {error !== null && <Alert color="red">{error}</Alert>}

          <Button
            disabled={roleId === null || roleId === target?.roleId}
            loading={assign.isPending}
            onClick={() => assign.mutate()}
          >
            Assign
          </Button>
        </Stack>
      </Modal>

      <Modal
        opened={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Invite someone"
      >
        <Stack gap="md">
          {inviteLink === null ? (
            <>
              <Text c="dimmed" fz="sm">
                There is no self sign-up. The person gets a one-time link that expires; you can only
                grant a role no higher than your own.
              </Text>
              <TextInput
                label="Email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.currentTarget.value)}
              />
              <TextInput
                label="Name"
                value={inviteName}
                onChange={(e) => setInviteName(e.currentTarget.value)}
              />
              <Select
                label="Role"
                data={assignable.map((r) => ({ value: r._id, label: r.name }))}
                value={inviteRoleId}
                onChange={setInviteRoleId}
              />
              {error !== null && <Alert color="red">{error}</Alert>}
              <Button
                loading={invite.isPending}
                disabled={inviteEmail === '' || inviteName === '' || inviteRoleId === null}
                onClick={() => invite.mutate()}
              >
                Create invitation
              </Button>
            </>
          ) : (
            <>
              <Alert color="blue">
                Email delivery is not wired up yet, so pass this link on yourself. It is shown once
                and cannot be recovered.
              </Alert>
              <TextInput
                label="Invitation link"
                readOnly
                value={inviteLink}
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button
                variant="default"
                onClick={() => void navigator.clipboard.writeText(inviteLink)}
              >
                Copy link
              </Button>
              <Button variant="subtle" color="gray" onClick={() => setInviteOpen(false)}>
                Done
              </Button>
            </>
          )}
        </Stack>
      </Modal>

      <Modal
        opened={impTarget !== null}
        onClose={() => setImpTarget(null)}
        title={`View the portal as ${impTarget?.displayName ?? ''}`}
      >
        <Stack gap="md">
          <Alert color="orange">
            You will see exactly what this person sees, and everything you do will be recorded
            against your own name. The organization can read this in the audit log.
          </Alert>
          <Textarea
            label="Reason"
            description="Stays in the audit log permanently"
            autosize
            minRows={2}
            value={impReason}
            onChange={(e) => setImpReason(e.currentTarget.value)}
            placeholder="Reproducing a report that the checklist will not submit"
          />
          {error !== null && <Alert color="red">{error}</Alert>}
          <Button
            loading={impersonate.isPending}
            disabled={impReason.trim().length < 8}
            onClick={() => impersonate.mutate()}
          >
            Start
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
